/**
 * IkigaiContext - Provides well-being inference, entity detection, and mission prioritization
 *
 * Sprint 11a: Wires @ownyou/ikigai to consumer app
 * Sprint 16b: Adds LLM entity detection pipeline with auto-trigger
 * v13 Section 2 - Ikigai Intelligence Layer
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
  type ReactNode,
} from 'react';
import {
  getExistingProfile,
  getIkigaiContextForAgent,
  calculateWellBeingScore,
  sortMissionsByWellBeing,
  awardMissionPoints,
  getUserPoints,
  getIkigaiTier,
  // Sprint 16b: Entity detection pipeline
  runDetectionPipeline,
  migrateExistingEntities,
  deriveDimensionScores,
  PipelineSkipError,
  listAllLoveEntities,
  upsertLoveEntity,
  deleteLoveEntity,
  createEmptyDetectionState,
  recordDetectionRun,
  recordEntityFeedback,
  sweepStaleMetrics,
  userConfirmBoost,
  initDualConfidence,
  type AnalyzerLLMClient,
  type LLMPrivacyTier,
  type ModelPreferences,
  type DetectionResult,
  type DerivedDimensions,
  type IkigaiProfile,
  type MissionCard,
  type UserPoints,
  type MissionWellBeingScore,
  type LoveEntity,
  // Sprint 17f: Cold start
  getFirstRunThresholds,
  getQuickWinOpportunities,
  type LoveSource,
  type MissionOpportunity,
  type DetectionState,
  type EntityFeedback,
  PipelineTracer,
} from '@ownyou/ikigai';
import { NS, NAMESPACES } from '@ownyou/shared-types';
import type { PipelineTrace } from '@ownyou/shared-types';
import type { BudgetManagerConfig } from '@ownyou/llm-client';
import { getAccessToken as getPrivyAccessToken } from '@privy-io/react-auth';
import { useStore } from './StoreContext';
import { useAuth } from './AuthContext';
import { useModelPreferences } from '../hooks/useModelPreferences';
import {
  createIkigaiRouteLlmClientAdapter,
  isLlmRouteCloudProvider,
  MODEL_ROUTE_IDS,
  resolveIkigaiRoutePlan,
  type IkigaiRouteLocalOutcomeEvent,
  type LlmRouteTelemetryEvent,
} from '../lib/llm-routing';
import { DEFAULT_REFRESH_CONFIG } from '../config/refresh-config';
import {
  createMissionSpineRunId,
  recordMissionSpineMarker,
} from '../lib/mission-spine/instrumentation';
import { dispatchOwnYouDataChanged } from '../lib/mission-spine/data-change-bridge';
import { readIkigaiSourceInput } from '../lib/ikigai/source-input';
import {
  persistIkigaiEntities,
  persistIkigaiOpportunities,
} from '../lib/ikigai/detection-persistence';

interface IkigaiContextValue {
  /** Current user's Ikigai profile */
  profile: IkigaiProfile | null;

  /** Loading state for profile */
  isProfileLoading: boolean;

  /** Get Ikigai context for an agent (returns formatted string) */
  getContextForAgent: () => Promise<string>;

  /** Calculate well-being score for a mission */
  scoreMission: (mission: MissionCard) => Promise<MissionWellBeingScore>;

  /** Sort missions by well-being alignment */
  sortMissions: <T extends MissionCard>(
    missions: T[]
  ) => Promise<Array<T & { wellBeingScore: MissionWellBeingScore }>>;

  /** Award points for completing a mission */
  awardPoints: (mission: MissionCard) => Promise<number>;

  /** Get user's total points */
  getPoints: () => Promise<UserPoints>;

  /** Get user's Ikigai tier (Bronze, Silver, Gold, Platinum) */
  getTier: () => Promise<{ tier: string; nextTierAt: number; progress: number }>;

  // Sprint 16b: Entity detection pipeline
  /** Detected love entities */
  entities: LoveEntity[];

  /** Whether entity detection is running */
  isDetecting: boolean;

  /** Derived dimension scores from entities */
  derivedDimensions: DerivedDimensions | null;

  /** Mission opportunities from detection */
  opportunities: MissionOpportunity[];

  /** Run entity detection pipeline manually */
  runEntityDetection: (opts?: { changedSources?: LoveSource[] }) => Promise<DetectionResult | null>;

  /** Record feedback on an entity */
  recordFeedback: (
    entityId: string,
    type: EntityFeedback['type'],
    metadata?: Record<string, string>
  ) => Promise<void>;

  /** Whether sync is in progress (for coalescing window) */
  isSyncInProgress: () => boolean;

  /** Whether cloud consent is needed (pipeline blocked on cloud model) */
  cloudConsentRequired: boolean;

  /** Grant cloud consent and re-run the pipeline */
  grantCloudConsent: () => void;

  /** Latest pipeline trace (for transparency UI) */
  latestTrace: PipelineTrace | null;

  /** Sprint 17e: Re-run entity detection if stale (> 4h since last run). Returns true if ran. */
  triggerRefreshIfStale: () => Promise<boolean>;
}

const IkigaiContext = createContext<IkigaiContextValue | null>(null);

type BudgetableStore = {
  get<T>(namespace: readonly [string, ...string[]], key: string): Promise<T | null>;
  put<T>(namespace: readonly [string, ...string[]], key: string, value: T): Promise<void>;
};

const DAILY_LLM_BUDGET_USD = 5;
const UNKNOWN_CLOUD_REQUEST_RESERVE_USD = 1;

const IKIGAI_OPERATION_MODEL_PREFERENCES: ModelPreferences = {
  sourceSummary: MODEL_ROUTE_IDS.ikigai.sourceSummary,
  entitySynthesis: MODEL_ROUTE_IDS.ikigai.entitySynthesis,
  selfAudit: MODEL_ROUTE_IDS.ikigai.selfAudit,
  entityComposition: MODEL_ROUTE_IDS.ikigai.entityComposition,
  profileSummary: MODEL_ROUTE_IDS.ikigai.profileSummary,
  fast: MODEL_ROUTE_IDS.ikigai.sourceSummary,
  standard: MODEL_ROUTE_IDS.ikigai.entitySynthesis,
};

function createBrowserLlmBudgetConfig(store: BudgetableStore): BudgetManagerConfig {
  return {
    monthlyBudgetUsd: 10,
    dailyBudgetUsd: DAILY_LLM_BUDGET_USD,
    unknownRequestReserveUsd: UNKNOWN_CLOUD_REQUEST_RESERVE_USD,
    throttling: {
      warnAt: 50,
      downgradeAt: 80,
      deferAt: 95,
      blockAt: 100,
    },
    store: {
      get: (namespace, userId, key) => store.get([namespace, userId], key),
      put: (namespace, userId, key, value) => store.put([namespace, userId], key, value),
    },
  };
}

function routePlanRequiresCloudConsent(
  routePlan: ReturnType<typeof resolveIkigaiRoutePlan>
): boolean {
  return Object.values(routePlan.routes).some(route => isLlmRouteCloudProvider(route?.provider));
}

function privacyTierForRoutePlan(
  routePlan: ReturnType<typeof resolveIkigaiRoutePlan>
): LLMPrivacyTier {
  const sourceProvider = routePlan.routes[MODEL_ROUTE_IDS.ikigai.sourceSummary]?.provider;
  if (!sourceProvider) return 'cloud-standard';
  if (!isLlmRouteCloudProvider(sourceProvider)) return 'local';
  return sourceProvider === 'anthropic' ? 'cloud-zdr' : 'cloud-standard';
}

function logIkigaiRouteTelemetry(event: LlmRouteTelemetryEvent): void {
  console.info('[IkigaiContext] Ikigai route telemetry:', {
    routeId: event.routeId,
    provider: event.provider,
    model: event.model,
    selectionSource: event.selectionSource,
    selectionReason: event.selectionReason,
    result: event.result,
    errorCode: event.errorCode,
    validation: event.validation,
    budgetStatus: event.budgetStatus,
    operation: event.operation,
  });
}

function logIkigaiRouteLocalOutcome(event: IkigaiRouteLocalOutcomeEvent): void {
  console.info('[IkigaiContext] Ikigai route-local outcome:', {
    routeId: event.routeId,
    operation: event.operation,
    provider: event.provider,
    model: event.model,
    selectionSource: event.selectionSource,
    selectionReason: event.selectionReason,
    status: event.status,
    result: event.result,
    errorCode: event.errorCode,
  });
}

/**
 * Derive MissionOpportunities from entities when the LLM doesn't generate them.
 * Uses a simple category→agentType heuristic matching the Pass 2 prompt's mapping guide.
 */
function deriveOpportunitiesFromEntities(entities: LoveEntity[]): MissionOpportunity[] {
  function inferAgentType(e: LoveEntity): MissionOpportunity['agentType'] {
    const all = `${e.name} ${e.category ?? ''} ${(e.subcategories ?? []).join(' ')}`.toLowerCase();
    if (/restaurant|dining|food|cuisine|culinary|cook/.test(all)) return 'restaurant';
    if (/travel|flight|hotel|vacation|trip/.test(all)) return 'travel';
    if (/concert|meetup|workshop|exhibition|festival|sport.*event|ticket/.test(all))
      return 'events';
    if (/budget|savings|invest|subscription|financial/.test(all)) return 'financial';
    if (/shop|deal|product|gear|equipment|clothing|furniture|garden/.test(all)) return 'shopping';
    return 'content';
  }

  return entities.slice(0, 15).map(e => ({
    entityName: e.name,
    description: `Discover options related to ${e.name}`,
    suggestedMission: `Find the best options for "${e.name}"`,
    agentType: inferAgentType(e),
    context: {
      ...(e.actionableContext?.location ? { location: e.actionableContext.location } : {}),
      ...(e.actionableContext?.budget ? { budget: e.actionableContext.budget } : {}),
    },
    createdAt: Date.now(),
    status: 'pending' as const,
  }));
}

interface IkigaiProviderProps {
  children: ReactNode;
  /** Sprint 16b: Function to check if data sync is in progress (from DataSourceContext) */
  isSyncInProgress?: () => boolean;
}

export function IkigaiProvider({ children, isSyncInProgress }: IkigaiProviderProps) {
  const { store, isReady } = useStore();
  const { wallet, isAuthenticated } = useAuth();
  const userId = wallet?.address ?? 'anonymous';

  const { routeSelections, models: availableModels } = useModelPreferences();
  const llmBudgetConfig = useMemo(
    () => (store ? createBrowserLlmBudgetConfig(store) : undefined),
    [store]
  );

  const [profile, setProfile] = useState<IkigaiProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  // Sprint 16b: Entity detection state
  const [entities, setEntities] = useState<LoveEntity[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [derivedDimensions, setDerivedDimensions] = useState<DerivedDimensions | null>(null);
  const [opportunities, setOpportunities] = useState<MissionOpportunity[]>([]);
  const [cloudConsentRequired, setCloudConsentRequired] = useState(false);
  const [latestTrace, setLatestTrace] = useState<PipelineTrace | null>(null);
  const detectionStateRef = useRef<DetectionState | null>(null);
  const lastRunIdRef = useRef<string>('');
  // C3 fix: Use ref for entities to avoid stale closures in async pipeline
  const entitiesRef = useRef<LoveEntity[]>([]);

  // Auto-trigger refs
  const pendingSourcesRef = useRef(new Set<LoveSource>());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetCountRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const failCountRef = useRef(0);
  const MAX_RESETS = 4;

  /**
   * Load existing profile from Store.
   */
  useEffect(() => {
    if (!isReady || !isAuthenticated || !store) {
      setIsProfileLoading(false);
      return;
    }

    const init = async () => {
      setIsProfileLoading(true);

      try {
        const existingProfile = await getExistingProfile(userId, store);
        if (existingProfile) {
          setProfile(existingProfile);
          console.log('[IkigaiContext] Loaded existing profile');
        }
      } catch (error) {
        console.error('[IkigaiContext] Initialization failed:', error);
      } finally {
        setIsProfileLoading(false);
      }
    };

    init();
  }, [isReady, isAuthenticated, store, userId]);

  /**
   * Get Ikigai context for an agent
   * Actual API: getIkigaiContextForAgent(userId, store) -> Promise<string>
   * Returns a formatted string summary of the user's Ikigai profile
   */
  const getContextForAgent = useCallback(async (): Promise<string> => {
    if (!store) {
      return 'No Ikigai profile available yet.';
    }

    return getIkigaiContextForAgent(userId, store);
  }, [store, userId]);

  /**
   * Calculate well-being score for a mission
   * Actual API: calculateWellBeingScore(mission, userId, store) -> Promise<MissionWellBeingScore>
   */
  const scoreMission = useCallback(
    async (mission: MissionCard): Promise<MissionWellBeingScore> => {
      if (!store) {
        // Return neutral score without store
        return {
          utilityScore: 0.5,
          experienceBoost: 0,
          relationshipBoost: 0,
          interestAlignment: 0,
          givingBoost: 0,
          totalScore: 0.5,
        };
      }

      return calculateWellBeingScore(mission, userId, store);
    },
    [store, userId]
  );

  /**
   * Sort missions by well-being alignment
   * Actual API: sortMissionsByWellBeing(missions, userId, store) -> Promise<ScoredMission[]>
   */
  const sortMissions = useCallback(
    async <T extends MissionCard>(
      missions: T[]
    ): Promise<Array<T & { wellBeingScore: MissionWellBeingScore }>> => {
      if (!store || missions.length === 0) {
        return missions.map(m => ({
          ...m,
          wellBeingScore: {
            utilityScore: 0.5,
            experienceBoost: 0,
            relationshipBoost: 0,
            interestAlignment: 0,
            givingBoost: 0,
            totalScore: 0.5,
          },
        }));
      }

      const result = await sortMissionsByWellBeing(missions, userId, store);
      return result as Array<T & { wellBeingScore: MissionWellBeingScore }>;
    },
    [store, userId]
  );

  /**
   * Award points for completing a mission
   * Actual API: awardMissionPoints(mission, userId, store) -> Promise<number>
   */
  const awardPoints = useCallback(
    async (mission: MissionCard): Promise<number> => {
      if (!store) {
        throw new Error('Store not ready');
      }

      const points = await awardMissionPoints(mission, userId, store);
      console.log(`[IkigaiContext] Awarded ${points} points for mission ${mission.id}`);
      return points;
    },
    [store, userId]
  );

  /**
   * Get user's total points
   * Actual API: getUserPoints(userId, store) -> Promise<UserPoints>
   */
  const getPoints = useCallback(async (): Promise<UserPoints> => {
    if (!store) {
      return { total: 0, explorer: 0, connector: 0, helper: 0, achiever: 0 };
    }

    return getUserPoints(userId, store);
  }, [store, userId]);

  /**
   * Get user's Ikigai tier based on points
   * Actual API: getIkigaiTier(totalPoints) -> { tier, nextTierAt, progress }
   */
  const getTier = useCallback(async (): Promise<{
    tier: string;
    nextTierAt: number;
    progress: number;
  }> => {
    if (!store) {
      return { tier: 'Bronze', nextTierAt: 100, progress: 0 };
    }

    const points = await getUserPoints(userId, store);
    return getIkigaiTier(points.total);
  }, [store, userId]);

  // ---------------------------------------------------------------------------
  // Sprint 16b: Entity Detection Pipeline
  // ---------------------------------------------------------------------------

  const createAnalyzerAdapter = useCallback(
    (
      routePlan: ReturnType<typeof resolveIkigaiRoutePlan>,
      authToken: string | null
    ): AnalyzerLLMClient =>
      createIkigaiRouteLlmClientAdapter({
        routePlan,
        authToken,
        userId,
        budgetConfig: llmBudgetConfig,
        modelCatalog: availableModels,
        emitTelemetry: logIkigaiRouteTelemetry,
        emitRouteLocalOutcome: logIkigaiRouteLocalOutcome,
      }),
    [availableModels, llmBudgetConfig, userId]
  );

  /**
   * Load detection state and existing entities on init.
   */
  useEffect(() => {
    if (!isReady || !isAuthenticated || !store) return;

    const loadEntities = async () => {
      try {
        const existing = await listAllLoveEntities(userId, store);

        // Quality cleanup: remove garbage entities from broken pipeline runs
        // (raw URLs, fragments, uncategorized entities with zero ikigai scores)
        const quality = existing.filter(e => {
          if (!e.category || e.category === 'Uncategorized') return false;
          if (e.name.includes('://') || e.name.includes('.com') || e.name.includes('.co.uk'))
            return false;
          if (e.name.length < 3 || e.name.length > 80) return false;
          if (/^[()[\]:,.\s]+$/.test(e.name)) return false;
          if (e.name.endsWith(':') || e.name.endsWith(')') || e.name.startsWith('(')) return false;
          const ik = e.ikigaiRelevance;
          if (
            ik &&
            (ik.love ?? 0) === 0 &&
            (ik.goodAt ?? 0) === 0 &&
            (ik.worldNeeds ?? 0) === 0 &&
            (ik.paidFor ?? 0) === 0
          )
            return false;
          return true;
        });
        if (quality.length < existing.length) {
          console.log(
            `[IkigaiContext] Filtered ${existing.length - quality.length} low-quality entities from display (kept in Store)`
          );
        }

        // Only delete truly broken entities (URLs, fragments, tiny names) — not just low-scoring ones
        const garbage = existing.filter(
          e =>
            e.name.includes('://') ||
            e.name.includes('.com') ||
            e.name.includes('.co.uk') ||
            /^[()[\]:,.\s]+$/.test(e.name) ||
            e.name.length < 3
        );
        for (const e of garbage) {
          await deleteLoveEntity(userId, e.type, e.id, store);
        }
        if (garbage.length > 0) {
          console.log(
            `[IkigaiContext] Purged ${garbage.length} garbage entities (URLs/fragments) from Store`
          );
        }

        // Dedup: entities sharing same type + category with significant word overlap
        // are duplicates (e.g., "Fine Dining & Restaurant Reservations" and
        // "Premium Dining & Fine Restaurant Experiences" — same type, same category, shared words)
        const deduped: typeof quality = [];
        const deleted: typeof quality = [];

        const sigWords = (name: string) =>
          name
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .split(/\s+/)
            .filter(w => w.length > 2);

        const wordSimilarity = (a: string, b: string): number => {
          const wordsA = sigWords(a);
          const wordsB = sigWords(b);
          if (wordsA.length === 0 || wordsB.length === 0) return 0;
          const shared = wordsA.filter(w => wordsB.some(bw => bw.includes(w) || w.includes(bw)));
          return shared.length / Math.min(wordsA.length, wordsB.length);
        };

        // Categories are "similar" if one contains the other (e.g., "home & garden" vs "home & garden technology")
        const similarCategory = (a: string, b: string): boolean => {
          if (a === b) return true;
          const na = a.toLowerCase(),
            nb = b.toLowerCase();
          return na.includes(nb) || nb.includes(na);
        };

        // Sort by confidence desc so we keep the best one
        const sorted = [...quality].sort((a, b) => b.confidence - a.confidence);
        for (const e of sorted) {
          const dup = deduped.find(
            kept =>
              kept.type === e.type &&
              similarCategory(kept.category, e.category) &&
              wordSimilarity(kept.name, e.name) >= 0.5
          );
          if (dup) {
            console.log(`[IkigaiContext] Dedup: "${e.name}" duplicates "${dup.name}", removing`);
            deleted.push(e);
          } else {
            deduped.push(e);
          }
        }
        if (deleted.length > 0) {
          for (const e of deleted) {
            await deleteLoveEntity(userId, e.type, e.id, store);
          }
          console.log(
            `[IkigaiContext] Dedup: ${deleted.length} duplicate entities removed from Store`
          );
        }

        // Check for first run / migration
        const needsMigration = deduped.some(e => !e.detectionRunId || e.detectionRunId === '');
        if (needsMigration && deduped.length > 0) {
          const migrated = migrateExistingEntities(deduped);
          setEntities(migrated);
          entitiesRef.current = migrated;
          // Persist migrated entities
          for (const entity of migrated) {
            await upsertLoveEntity(userId, entity, store);
          }
          console.log(
            `[IkigaiContext] Migrated ${migrated.length} existing entities to 16b format`
          );
        } else {
          setEntities(deduped);
          entitiesRef.current = deduped;
        }

        // Load detection state
        try {
          const stateNs = NS.ikigaiDetectionState(userId);
          // If all entities were purged, reset state so next run is treated as first run
          if (deduped.length === 0 && existing.length > 0) {
            console.log(
              '[IkigaiContext] All entities purged — resetting detection state for fresh start'
            );
            detectionStateRef.current = createEmptyDetectionState();
            await store.put(stateNs, 'current', createEmptyDetectionState());
          } else {
            const stateItem = await store.get<DetectionState>(stateNs, 'current');
            if (stateItem) {
              detectionStateRef.current = stateItem;
            }
          }
        } catch {
          // No existing state — first run
        }

        // Derive dimension scores from entities
        if (deduped.length > 0) {
          const dims = deriveDimensionScores(deduped);
          setDerivedDimensions(dims);
        }
      } catch (error) {
        console.error('[IkigaiContext] Failed to load entities:', error);
      }
    };

    loadEntities();
  }, [isReady, isAuthenticated, store, userId]);

  /**
   * Run the entity detection pipeline.
   */
  const runEntityDetection = useCallback(
    async (opts?: { changedSources?: LoveSource[] }): Promise<DetectionResult | null> => {
      if (!store) return null;

      let authToken: string | null = null;
      try {
        authToken = await getPrivyAccessToken();
      } catch {
        authToken = null;
      }

      const routePlan = resolveIkigaiRoutePlan({
        routeSelections,
        models: availableModels,
        authToken,
      });
      const missionSpineRunId = createMissionSpineRunId('ikigai-detection');

      recordMissionSpineMarker({
        runId: missionSpineRunId,
        hop: 'ikigai_detection_start',
        status: 'start',
        reasonCode: 'detection-requested',
        counts: {
          availableRouteCount: Object.keys(routePlan.routes).length,
          unavailableRouteCount: routePlan.unavailable.length,
        },
      });

      const sourceSummaryRoute = routePlan.routes[MODEL_ROUTE_IDS.ikigai.sourceSummary];
      const entitySynthesisRoute = routePlan.routes[MODEL_ROUTE_IDS.ikigai.entitySynthesis];
      if (!sourceSummaryRoute || !entitySynthesisRoute) {
        recordMissionSpineMarker({
          runId: missionSpineRunId,
          hop: 'ikigai_route_plan',
          status: 'blocked',
          reasonCode: 'required-route-unavailable',
          counts: {
            availableRouteCount: Object.keys(routePlan.routes).length,
            unavailableRouteCount: routePlan.unavailable.length,
          },
        });
        console.warn('[IkigaiContext] Required Ikigai routes are unavailable', {
          unavailable: routePlan.unavailable.map(route => ({
            routeId: route.routeId,
            reason: route.reason,
            savedSelection: route.savedSelection,
          })),
        });
        return null;
      }
      recordMissionSpineMarker({
        runId: missionSpineRunId,
        hop: 'ikigai_route_plan',
        status: 'success',
        reasonCode: 'required-routes-available',
        counts: {
          availableRouteCount: Object.keys(routePlan.routes).length,
          unavailableRouteCount: routePlan.unavailable.length,
        },
      });

      setIsDetecting(true);

      try {
        // Sweep stale metrics at pipeline start
        await sweepStaleMetrics(userId, store);

        const state = detectionStateRef.current ?? createEmptyDetectionState();
        // C3 fix: Read from ref to avoid stale closure
        const existingEntities = entitiesRef.current;

        const privacyTier = privacyTierForRoutePlan(routePlan);
        const prefs: ModelPreferences = {
          ...IKIGAI_OPERATION_MODEL_PREFERENCES,
          privacyTier,
        };
        const sourceSummaryModel = MODEL_ROUTE_IDS.ikigai.sourceSummary;
        const entitySynthesisModel = MODEL_ROUTE_IDS.ikigai.entitySynthesis;

        if (routePlanRequiresCloudConsent(routePlan) && !state.cloudConsent) {
          window.dispatchEvent(
            new CustomEvent('cloud-consent-required', {
              detail: {
                routes: Object.values(routePlan.routes)
                  .map(
                    route =>
                      route && {
                        routeId: route.routeId,
                        provider: route.provider,
                        model: route.model,
                        selectionSource: route.selectionSource,
                      }
                  )
                  .filter(Boolean),
                tier: 'cloud',
              },
            })
          );
          console.warn('[IkigaiContext] Cloud consent required — waiting for user approval');
          return null;
        }

        const analyzerLlm = createAnalyzerAdapter(routePlan, authToken);

        console.log(`[IkigaiContext] Loading source data for userId: ${userId}`);

        const filterNewsletters = (() => {
          try {
            const saved = localStorage.getItem('ownyou_privacy_toggles');
            if (saved) return JSON.parse(saved).filterNewsletters === true;
          } catch {
            /* ignore */
          }
          return false;
        })();

        const {
          sourceData,
          summary: sourceInputSummary,
          diagnostics: loadDiag,
        } = await readIkigaiSourceInput({ store, userId, filterNewsletters });
        const { counts } = sourceInputSummary;
        const loadedSources = sourceInputSummary.loadedSources;
        if (counts.emailCount > 0) {
          console.log(`[IkigaiContext] Loaded ${counts.emailCount} emails from Store`);
        }
        if (counts.calendarCount > 0) {
          console.log(`[IkigaiContext] Loaded ${counts.calendarCount} calendar events from Store`);
        }
        if (counts.financialCount > 0) {
          console.log(`[IkigaiContext] Loaded ${counts.financialCount} transactions from Store`);
        }
        if (counts.browsingCount > 0) {
          console.log(`[IkigaiContext] Loaded ${counts.browsingCount} browsing entries from Store`);
        }
        for (const issue of loadDiag.errors) {
          console.warn(
            `[IkigaiContext] Failed to load ${issue.source} source input from Store:`,
            issue.message,
          );
        }
        console.log(`[IkigaiContext] Source data loaded: ${loadedSources.join(', ') || 'none'}`);
        recordMissionSpineMarker({
          runId: missionSpineRunId,
          hop: 'ikigai_source_input',
          status: loadedSources.length > 0 ? 'success' : 'skipped',
          reasonCode: loadedSources.length > 0 ? 'source-input-loaded' : 'no-source-input',
          counts,
        });

        // Create pipeline tracer for transparency
        const tracer = new PipelineTracer({
          trigger: 'manual',
          modelConfig: {
            fast: sourceSummaryModel,
            standard: entitySynthesisModel,
            privacyTier: String(privacyTier ?? 'cloud-standard'),
          },
          previewMode: 'redacted',
        });

        const result = await runDetectionPipeline(
          sourceData,
          existingEntities,
          state,
          analyzerLlm,
          prefs,
          undefined, // budget
          opts?.changedSources,
          undefined, // feedbackByEntity
          tracer,
          userId,
          store
        );

        // Update state
        setEntities(result.entities);
        entitiesRef.current = result.entities;
        detectionStateRef.current = result.state;
        lastRunIdRef.current = result.metrics.runId;

        // Derive dimension scores
        if (result.entities.length > 0) {
          const dims = deriveDimensionScores(result.entities);
          setDerivedDimensions(dims);
        }

        // Record metrics
        await recordDetectionRun(userId, result.metrics, store);

        // Persist detection state
        const stateNs = NS.ikigaiDetectionState(userId);
        await store.put(stateNs, 'current', result.state);

        // Persist entities
        const entityStoreWriteCount = await persistIkigaiEntities(userId, result.entities, store);

        // If LLM returned entities but no opportunities, derive them deterministically
        if (result.opportunities.length === 0 && result.entities.length > 0) {
          console.log(
            `[IkigaiContext] LLM returned 0 opportunities for ${result.entities.length} entities — deriving from entities`
          );
          result.opportunities = deriveOpportunitiesFromEntities(result.entities);
        }

        // Sprint 17f Task 3.10: Cold start quick wins for sparse data
        if (result.entities.length < 3) {
          const thresholds = getFirstRunThresholds(result.entities.length);
          console.log(
            `[IkigaiContext] Cold start: ${result.entities.length} entities, using threshold=${thresholds.confidenceThreshold}`
          );
          // Generate quick wins from connected data sources
          const quickWins = getQuickWinOpportunities({});
          if (quickWins.length > 0) {
            result.opportunities.push(...quickWins);
          }
        }

        // Handle opportunities (I6 fix: filter expired and cap at 50)
        if (result.opportunities.length > 0) {
          const MAX_OPPORTUNITIES = 50;
          setOpportunities(prev => {
            const now = Date.now();
            const combined = [...prev, ...result.opportunities]
              .filter(o => !o.deadline || new Date(o.deadline).getTime() > now)
              .filter(o => o.status !== 'expired');
            return combined.slice(-MAX_OPPORTUNITIES);
          });

          const opportunityStoreWriteCount = await persistIkigaiOpportunities(
            userId,
            result.opportunities,
            store,
          );
          recordMissionSpineMarker({
            runId: missionSpineRunId,
            hop: 'ikigai_opportunity_write',
            status: 'success',
            namespace: NAMESPACES.IKIGAI_OPPORTUNITIES,
            sourceId: 'love-entity-pipeline',
            reasonCode: 'opportunities-persisted',
            counts: {
              entityCount: result.entities.length,
              entityStoreWriteCount,
              opportunityCount: result.opportunities.length,
              opportunityStoreWriteCount,
            },
          });

          // Emit custom event for UI
          window.dispatchEvent(
            new CustomEvent('opportunities-detected', {
              detail: { opportunities: result.opportunities },
            })
          );

          // CRITICAL: Notify agent worker that new opportunities are available.
          // This bridges the IkigaiContext → agent.worker.ts pipeline so agents
          // consume Love Entity opportunities (NOT IAB classifications).
          dispatchOwnYouDataChanged({
            namespace: NAMESPACES.IKIGAI_OPPORTUNITIES,
            sourceId: 'love-entity-pipeline',
            runId: missionSpineRunId,
          });

          // Toast for urgent opportunities (within 7 days)
          for (const opp of result.opportunities) {
            if (opp.deadline) {
              const daysUntil = (new Date(opp.deadline).getTime() - Date.now()) / 86_400_000;
              if (daysUntil <= 7 && daysUntil > 0) {
                window.dispatchEvent(
                  new CustomEvent('toast', {
                    detail: {
                      message: `Urgent: ${opp.description}`,
                      type: 'opportunity',
                      entityName: opp.entityName,
                    },
                  })
                );
              }
            }
          }
        } else {
          recordMissionSpineMarker({
            runId: missionSpineRunId,
            hop: 'ikigai_opportunity_write',
            status: 'skipped',
            namespace: NAMESPACES.IKIGAI_OPPORTUNITIES,
            sourceId: 'love-entity-pipeline',
            reasonCode: 'no-opportunities-generated',
            counts: {
              entityCount: result.entities.length,
              entityStoreWriteCount,
              opportunityCount: 0,
              opportunityStoreWriteCount: 0,
            },
          });
        }

        // Toast for new entities
        const newEntities = result.entities.filter(e => e.trend === 'new');
        if (newEntities.length > 0) {
          window.dispatchEvent(
            new CustomEvent('toast', {
              detail: {
                message: `Discovered ${newEntities.length} new ${newEntities.length === 1 ? 'interest' : 'interests'}`,
                type: 'entity',
              },
            })
          );
        }

        // Finalize and persist pipeline trace
        const trace = tracer.finalize(result.entities.length > 0 ? 'success' : 'partial');
        setLatestTrace(trace);
        console.log(
          `[PipelineTracer] Trace: ${trace.steps.length} steps, cost $${trace.costSummary.totalUsd.toFixed(4)}, diff: +${trace.entityDiff.added.length}/-${trace.entityDiff.removed.length}`
        );

        // Persist trace to Store
        try {
          const traceNs = NS.pipelineTraces(userId);
          await store.put(traceNs, trace.id, trace);
        } catch (e) {
          console.warn('[IkigaiContext] Failed to persist pipeline trace:', e);
        }

        // Write trace + result to disk for CLI inspection
        try {
          const traceOutput = JSON.stringify(
            {
              trace,
              result: {
                entities: result.entities,
                opportunities: result.opportunities,
                metrics: result.metrics,
              },
              input: {
                sourceData: Object.fromEntries(
                  Object.entries(sourceData).map(([k, v]) => [k, Array.isArray(v) ? v.length : 0])
                ),
                modelPrefs: prefs,
                privacyTier: String(privacyTier ?? 'cloud-standard'),
              },
              loadDiag,
            },
            null,
            2
          );

          if (window.__TAURI_INTERNALS__) {
            // Tauri: write via Rust command
            const { invoke } = await import('@tauri-apps/api/core');
            await invoke('write_pipeline_trace', {
              path: '/tmp/ownyou-pipeline-latest.json',
              content: traceOutput,
            });
          } else {
            // PWA dev: POST to Vite dev server plugin
            await fetch('/__dev/pipeline-trace', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: traceOutput,
            });
          }
          console.log('[IkigaiContext] Pipeline trace written to /tmp/ownyou-pipeline-latest.json');
        } catch (e) {
          console.warn('[IkigaiContext] Failed to write pipeline trace to disk:', e);
        }

        console.log(
          `[IkigaiContext] Detection complete: ${result.entities.length} entities, ${result.opportunities.length} opportunities`
        );
        return result;
      } catch (error) {
        if (error instanceof PipelineSkipError) {
          console.log('[IkigaiContext] Pipeline skipped:', error.message);
          return null; // Skips are normal (no new data, budget exceeded) — not errors
        }
        console.error('[IkigaiContext] Detection failed:', error);
        throw error;
      } finally {
        setIsDetecting(false);
      }
    },
    [availableModels, createAnalyzerAdapter, routeSelections, store, userId]
  );

  /**
   * Record user feedback on an entity.
   */
  const recordFeedback = useCallback(
    async (entityId: string, type: EntityFeedback['type'], metadata?: Record<string, string>) => {
      if (!store) return;

      const feedback: EntityFeedback = {
        entityId,
        runId: lastRunIdRef.current,
        type,
        timestamp: Date.now(),
        metadata,
      };

      await recordEntityFeedback(userId, feedback, store);

      // If confirm type, boost confidence via dual confidence model
      if (type === 'confirm') {
        const entity = entitiesRef.current.find(e => e.id === entityId);
        if (entity) {
          const dual = entity.dualConfidence ?? initDualConfidence(entity.confidence);
          const boosted = userConfirmBoost(dual);
          const updated = {
            ...entity,
            confidence: boosted.finalConfidence,
            dualConfidence: boosted,
          };
          await upsertLoveEntity(userId, updated, store);
          setEntities(prev => prev.map(e => (e.id === entityId ? updated : e)));
          entitiesRef.current = entitiesRef.current.map(e => (e.id === entityId ? updated : e));
        }
      }
    },
    [store, userId]
  );

  /**
   * Sprint 16b: Auto-trigger — listen for data-changed events
   */
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ source: LoveSource }>;
      // Skip if Store is unavailable or in cooldown.
      if (!store) return;
      if (Date.now() < cooldownUntilRef.current) return;

      // Track which source has new data
      pendingSourcesRef.current.add(customEvent.detail.source);

      // Reset or start the coalescing timer
      if (timerRef.current) clearTimeout(timerRef.current);

      const triggerOrWait = async () => {
        // Check if any syncs are still in progress
        const syncing = isSyncInProgress?.() ?? false;

        if (syncing && resetCountRef.current < MAX_RESETS) {
          resetCountRef.current++;
          timerRef.current = setTimeout(triggerOrWait, 15_000);
          return;
        }

        // Run pipeline with all pending sources
        const sources = [...pendingSourcesRef.current];
        pendingSourcesRef.current.clear();
        resetCountRef.current = 0;

        try {
          await runEntityDetection({ changedSources: sources });
          failCountRef.current = 0;
        } catch (error) {
          if (error instanceof PipelineSkipError) {
            const backoff = Math.min(16 * 60_000, 60_000 * Math.pow(4, failCountRef.current));
            cooldownUntilRef.current = Date.now() + backoff;
            failCountRef.current++;
          }
        }
      };

      timerRef.current = setTimeout(triggerOrWait, 30_000);
    };

    window.addEventListener('data-changed', handler);
    return () => {
      window.removeEventListener('data-changed', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [runEntityDetection, isSyncInProgress, store]);

  /**
   * Sprint 16b: Listen for cloud-consent-required to track banner state,
   * and cloud-consent-granted to update consent + re-run pipeline.
   */
  useEffect(() => {
    const onConsentRequired = () => {
      setCloudConsentRequired(true);
    };

    const onConsentGranted = async () => {
      setCloudConsentRequired(false);

      // Update detection state with consent
      const state = detectionStateRef.current ?? createEmptyDetectionState();
      state.cloudConsent = true;
      detectionStateRef.current = state;

      // Persist updated state
      if (store) {
        try {
          const stateNs = NS.ikigaiDetectionState(userId);
          await store.put(stateNs, 'current', state);
        } catch (e) {
          console.warn('[IkigaiContext] Failed to persist cloud consent:', e);
        }
      }

      // Re-run the pipeline now that consent is granted
      try {
        await runEntityDetection();
      } catch {
        // Pipeline skip or error — handled by context
      }
    };

    window.addEventListener('cloud-consent-required', onConsentRequired);
    window.addEventListener('cloud-consent-granted', onConsentGranted);
    return () => {
      window.removeEventListener('cloud-consent-required', onConsentRequired);
      window.removeEventListener('cloud-consent-granted', onConsentGranted);
    };
  }, [store, userId, runEntityDetection]);

  const grantCloudConsent = useCallback(() => {
    window.dispatchEvent(new CustomEvent('cloud-consent-granted'));
  }, []);

  const isSyncInProgressFn = useCallback(() => isSyncInProgress?.() ?? false, [isSyncInProgress]);

  // Sprint 17e: Re-run entity detection if stale (guard from refresh config)
  const triggerRefreshIfStale = useCallback(async (): Promise<boolean> => {
    const LS_KEY = 'ownyou_last_pipeline_run';
    const lastRun = localStorage.getItem(LS_KEY);
    const lastRunTime = lastRun ? parseInt(lastRun, 10) : 0;

    if (Date.now() - lastRunTime < DEFAULT_REFRESH_CONFIG.pipelineRefreshGuardMs) {
      return false; // Not stale
    }

    try {
      await runEntityDetection();
      localStorage.setItem(LS_KEY, String(Date.now()));
      return true;
    } catch (err) {
      console.warn('[IkigaiContext] Refresh pipeline failed:', err);
      return false;
    }
  }, [runEntityDetection]);

  const value: IkigaiContextValue = {
    profile,
    isProfileLoading,
    getContextForAgent,
    scoreMission,
    sortMissions,
    awardPoints,
    getPoints,
    getTier,
    // Sprint 16b
    entities,
    isDetecting,
    derivedDimensions,
    opportunities,
    runEntityDetection,
    recordFeedback,
    isSyncInProgress: isSyncInProgressFn,
    cloudConsentRequired,
    grantCloudConsent,
    latestTrace,
    triggerRefreshIfStale,
  };

  return <IkigaiContext.Provider value={value}>{children}</IkigaiContext.Provider>;
}

export function useIkigai() {
  const context = useContext(IkigaiContext);
  if (!context) {
    throw new Error('useIkigai must be used within an IkigaiProvider');
  }
  return context;
}

/**
 * Convert DerivedDimensions (simple numbers) to DimensionScores (rich objects)
 * so the mission ranker can use them for Ikigai-aligned sorting.
 */
export function derivedToDimensionScores(
  derived: DerivedDimensions | null
): import('@ownyou/ikigai').DimensionScores | null {
  if (!derived) return null;
  const dims = ['passion', 'mission', 'vocation', 'profession'] as const;
  const result: Record<
    string,
    {
      score: number;
      confidence: number;
      dataPointCount: number;
      signals: string[];
      trend: 'stable';
    }
  > = {};
  for (const dim of dims) {
    result[dim] = {
      score: derived[dim],
      confidence: derived[dim] > 0 ? 0.7 : 0,
      dataPointCount: 0,
      signals: [],
      trend: 'stable' as const,
    };
  }
  return result as import('@ownyou/ikigai').DimensionScores;
}

/**
 * Hook for mission scoring and sorting
 * Returns synchronous wrappers that cache results
 */
export function useMissionScoring() {
  const { sortMissions, profile, isProfileLoading, derivedDimensions } = useIkigai();
  const { store } = useStore();

  // For synchronous sort needed by useMissions, we provide a wrapper
  // that sorts by priority when store isn't available
  const sort = useCallback(
    <T extends MissionCard>(missions: T[]): T[] => {
      if (!store || !profile) {
        // Fall back to priority-based sorting
        return [...missions].sort(
          (a, b) =>
            ((a as unknown as { priority?: number }).priority ?? 0) -
            ((b as unknown as { priority?: number }).priority ?? 0)
        );
      }
      // For async sorting, caller should use sortMissions directly
      return missions;
    },
    [store, profile]
  );

  const getDimensionScores = useCallback(() => {
    return derivedToDimensionScores(derivedDimensions);
  }, [derivedDimensions]);

  return {
    sort,
    sortAsync: sortMissions,
    hasProfile: !!profile,
    isLoading: isProfileLoading,
    getDimensionScores,
  };
}

/**
 * Hook for user rewards/points
 */
export function useIkigaiRewards() {
  const { awardPoints, getPoints, getTier } = useIkigai();
  const [points, setPoints] = useState<UserPoints>({
    total: 0,
    explorer: 0,
    connector: 0,
    helper: 0,
    achiever: 0,
  });
  const [tier, setTier] = useState<{ tier: string; nextTierAt: number; progress: number }>({
    tier: 'Bronze',
    nextTierAt: 100,
    progress: 0,
  });

  const refresh = useCallback(async () => {
    const [p, t] = await Promise.all([getPoints(), getTier()]);
    setPoints(p);
    setTier(t);
  }, [getPoints, getTier]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    points,
    tier,
    awardPoints,
    refresh,
  };
}
